import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import {
  ThemeProvider, Typography, Divider, CircularProgress, Link,
} from '@material-ui/core';

import { useLazyQuery } from '@apollo/client';

import { useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';
import {
  AccessibleButton,
  ContentContainer,
  FlexContainer,
  HeaderBox,
  Title,
  DividerContainer,
  DividerText,
  continueButtonStyles,
} from './AuthMethodSelection.styles';

import sleepioTheme from '@/themes/sleepioTheme';
import SocialButton from '@/components/SocialButton/SocialButton';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { useConsent } from '@/hooks/useConsent';
import { loginUser } from '@/utils/sparkActivationApi';
import { logError } from '@/utils/logError';
import { buildRedirectParam } from '@/utils/urlHelpers';
import { FORGOT_PASSWORD_PATH } from '@/utils/passwordRoutes';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { linkPostHogIdentity, SLEEPIO_CANOPY_PROGRAM } from '@/utils/posthog';
import { trackSignInMethodSelected } from '@/utils/signInAnalytics';
import SleepioAccountIcon from '@/assets/SleepioAccountIcon.svg';

/**
 * "How would you like to log in?" — the step between entering an address and
 * finishing the sign-in.
 *
 * Both options are offered to everyone, and that is a security property rather than
 * a simplification. Deciding what to show by looking the address up first would tell
 * anyone who typed an address whether that person is a member — the same disclosure
 * the shared failure message exists to prevent, moved one screen earlier and made
 * cheaper to probe. So nothing on this screen issues a request keyed to the address.
 *
 * Sign-up never reaches here. A member creating an account has no password to use,
 * and the emailed link it sends enrolls them; sign-up is left exactly as it ships.
 */
const EmailAuthChoiceInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();

  const { t } = useTranslation();
  const history = useHistory();
  const base = useActivationBase();
  const { state, dispatch } = useActivation();
  const posthog = usePostHog();
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, { fetchPolicy: 'network-only' });

  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const { email } = state;
  const redirectParam = buildRedirectParam(state.redirectTo);

  useEffect(() => {
    dispatch({ type: 'SET_STEP', step: 'email_auth_choice' });
  }, [dispatch]);

  // Reached without an address — a refresh, or a pasted URL. Send them back to
  // collect it rather than rendering a choice about nothing.
  useEffect(() => {
    if (!email) {
      history.replace(`${base}/email${redirectParam}`);
    }
  }, [email, history, base, redirectParam]);

  if (!email) { return null; }

  const handleEmailLink = async () => {
    setIsSending(true);
    setSendError('');
    trackSignInMethodSelected(posthog, 'magic_link');
    try {
      // Returning-user login: send the link WITHOUT re-enrolling, exactly as the
      // email step did before this screen existed.
      await loginUser({
        email,
        product: 'sleepio',
        ph_distinct_id: posthog?.get_distinct_id() || undefined,
      });

      // Non-blocking: don't delay navigation for the analytics lookup.
      linkPostHogIdentity(getDistinctId, posthog, email, '[EmailAuthChoice]');

      dispatch({ type: 'SET_AUTH_METHOD', authMethod: 'email' });
      dispatch({ type: 'SET_STEP', step: 'magic_link_sent' });
      history.push(`${base}/sent${redirectParam}`);
    } catch (err) {
      logError('[EmailAuthChoice]', 'loginUser failed', err);
      posthog?.capture('ActivationFlowErrored', { program: SLEEPIO_CANOPY_PROGRAM });
      setSendError(t('coverage.pre_onboarding.sleepio.activation.error_send_failed'));
      setIsSending(false);
    }
  };

  const handleUsePassword = () => {
    trackSignInMethodSelected(posthog, 'password');
    dispatch({ type: 'SET_AUTH_METHOD', authMethod: 'email_password' });
    dispatch({ type: 'SET_STEP', step: 'email_password' });
    history.push(`${base}/email/password${redirectParam}`);
  };

  return (
    <ContentContainer>
      <SleepioHeader />
      <FlexContainer maxWidth="xs">
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.email_choose.title')}
          </Title>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.email_choose.sub_title')}
          </Typography>
        </HeaderBox>

        <AccessibleButton
          variant="contained"
          data-testid="email-link-button"
          type="button"
          fullWidth
          color="primary"
          rounded
          disabled={isSending}
          style={continueButtonStyles}
          onClick={handleEmailLink}
        >
          {isSending ? (
            <>
              <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
              {t('coverage.pre_onboarding.sleepio.activation.sending')}
            </>
          ) : t('coverage.pre_onboarding.sleepio.activation.email_choose.email_link')}
        </AccessibleButton>

        {sendError && (
          <Typography color="error" data-testid="send-error-message" style={{ marginTop: 8 }}>
            {sendError}
          </Typography>
        )}

        <DividerContainer>
          <Divider />
          <DividerText>
            {t('coverage.pre_onboarding.sleepio.activation.or')}
          </DividerText>
        </DividerContainer>

        {/* Inert while a link send is in flight. Leaving it live lets the member start
            typing a password and then get thrown onto the "check your email" screen when
            the earlier request resolves — and the unmount reports them as abandoned. */}
        <SocialButton
          icon={<img src={SleepioAccountIcon} alt="" width={20} height={20} />}
          labelKey="coverage.pre_onboarding.sleepio.activation.email_choose.use_password"
          testId="use-password-button"
          onClick={handleUsePassword}
          marginBottom={16}
          disabled={isSending}
        />

        <Typography align="center">
          <Link
            component="button"
            type="button"
            data-testid="forgot-password-link"
            disabled={isSending}
            // The address travels in router state, never in the URL — an email address
            // in a query string ends up in server logs and analytics payloads.
            onClick={() => history.push({ pathname: FORGOT_PASSWORD_PATH, state: { email } })}
          >
            {t('coverage.pre_onboarding.sleepio.activation.email_choose.forgot_password')}
          </Link>
        </Typography>

        <ConsentWidget />
      </FlexContainer>
      <Footer />
    </ContentContainer>
  );
};

export const EmailAuthChoice: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <EmailAuthChoiceInner />
  </ThemeProvider>
);

export default EmailAuthChoice;
